import { useRef, useState, useCallback, useEffect } from "react";

const SCROLL_THRESHOLD = 100; // px from bottom to consider "at bottom"

export function useSmartScroll() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [newMessageCount, setNewMessageCount] = useState(0);
  const userScrolledUp = useRef(false);
  const lastScrollTop = useRef(0);

  const checkIsAtBottom = useCallback(() => {
    const el = containerRef.current;
    if (!el) return true;
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    return distFromBottom <= SCROLL_THRESHOLD;
  }, []);

  const scrollToBottom = useCallback((smooth = true) => {
    const el = containerRef.current;
    if (!el) return;
    el.scrollTo({
      top: el.scrollHeight,
      behavior: smooth ? "smooth" : "instant",
    });
    userScrolledUp.current = false;
    setIsAtBottom(true);
    setNewMessageCount(0);
  }, []);

  // Force scroll (for user-initiated sends)
  const forceScrollToBottom = useCallback(() => {
    userScrolledUp.current = false;
    setNewMessageCount(0);
    // Use requestAnimationFrame to ensure DOM has updated
    requestAnimationFrame(() => {
      scrollToBottom(false);
    });
  }, [scrollToBottom]);

  // Handle scroll events
  const handleScroll = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;

    const atBottom = checkIsAtBottom();
    setIsAtBottom(atBottom);

    // Detect user scrolling up (not programmatic scroll)
    if (el.scrollTop < lastScrollTop.current && !atBottom) {
      userScrolledUp.current = true;
    }

    if (atBottom) {
      userScrolledUp.current = false;
      setNewMessageCount(0);
    }

    lastScrollTop.current = el.scrollTop;
  }, [checkIsAtBottom]);

  // Auto-scroll on content change if user hasn't scrolled up
  const autoScroll = useCallback(() => {
    if (!userScrolledUp.current) {
      requestAnimationFrame(() => {
        const el = containerRef.current;
        if (el) {
          el.scrollTop = el.scrollHeight;
          setIsAtBottom(true);
        }
      });
    } else {
      setNewMessageCount((c) => c + 1);
    }
  }, []);

  // Observe content changes for auto-scroll
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new ResizeObserver(() => {
      if (!userScrolledUp.current) {
        el.scrollTop = el.scrollHeight;
      }
    });

    // Observe the scroll content wrapper
    if (el.firstElementChild) {
      observer.observe(el.firstElementChild);
    }

    return () => observer.disconnect();
  }, []);

  // Attach scroll listener
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => el.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  return {
    containerRef,
    isAtBottom,
    newMessageCount,
    scrollToBottom: () => scrollToBottom(true),
    forceScrollToBottom,
    autoScroll,
  };
}
